import { forwardRef } from "react";

import Link from "next/link";

import { Button, Anchor } from "@newsela/angelou";
import PropTypes from "prop-types";

import withTrackEvent from "@/eventStream/withTrackEvent";
import formatHrefToSupportLegacyLink from "@/utils/formatHrefToSupportLegacyLink";
import { clickableRowCss } from "./ClickableRow.style";

const propTypes = {
  href: PropTypes.string,
  className: PropTypes.string,
  lang: PropTypes.string,
  onBlur: PropTypes.func,
  onClick: PropTypes.func,
  children: PropTypes.node,
  label: PropTypes.string,
  dataProps: PropTypes.object,
  trackImpression: PropTypes.bool,
  openInNewTab: PropTypes.bool,
  streamProperties: PropTypes.object,
};
const ButtonWithTrackEvent = withTrackEvent(Button);
const LinkWithTrackEvent = withTrackEvent(Link);

// `ClickableRow` renders arbitrary children content inside either a link or a
// button, based on whether there's a provided `href` prop. The visual
// appearance is intended to be independent of whether `ClickableRow` renders a
// link or a button.
const ClickableRow = forwardRef(
  (
    {
      openInNewTab,
      href,
      lang,
      className,
      onBlur,
      onClick,
      children,
      label,
      dataProps,
      streamProperties,
      trackImpression = true,
    },
    ref,
  ) => {
    let objectType;
    let actionPrefix;
    let actionName;
    let finalStreamProperties;

    if (label === "Add to Assignment" || label === "Create Assignment") {
      objectType = "Assignment";
      actionPrefix = "Initiate";
      actionName = "Navigate";
    }
    if (label === "Create Assignment") {
      finalStreamProperties = {
        ...streamProperties,
        description: "Opens Create Assignment page",
      };
    } else if (label === "Add to Assignment") {
      finalStreamProperties = {
        ...streamProperties,
        description: "Opens Add to Assignment Modal",
      };
    }
    return href ? (
      <LinkWithTrackEvent
        href={formatHrefToSupportLegacyLink(href)}
        passHref
        legacyBehavior
        objectType={objectType}
        actionPrefix={actionPrefix}
        actionName={actionName}
        streamProperties={streamProperties}
        trackImpression={trackImpression}
      >
        <Anchor
          dataProps={dataProps}
          ariaProps={{ lang }}
          ref={ref}
          __classNameFor={{
            root: className,
          }}
          __cssFor={{
            root: clickableRowCss,
          }}
          onClick={onClick}
          onBlur={onBlur}
        >
          {children}
        </Anchor>
      </LinkWithTrackEvent>
    ) : (
      <ButtonWithTrackEvent
        dataProps={dataProps}
        ref={ref}
        __classNameFor={{ root: className }}
        __cssFor={{ root: clickableRowCss }}
        /* AUTOGENERATED TODO: update angelou to new flavor.
          see https://github.com/newsela/angelou/blob/main/src/components/Button/README.md#MIGRATION
          for migration guide. */
        legacy_flavor={Button.legacy_flavor.incognito}
        onClick={onClick}
        objectType={objectType}
        actionPrefix={actionPrefix}
        actionName={actionName}
        streamProperties={finalStreamProperties}
        trackImpression={trackImpression}
        onBlur={onBlur}
        {...(openInNewTab || null)}
      >
        {children}
      </ButtonWithTrackEvent>
    );
  },
);

ClickableRow.propTypes = propTypes;

export default ClickableRow;
